import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wand2, AlertTriangle, FileText, X } from "lucide-react";
import { MergeFormConfig, FieldConfig, MergeAction, MergePreview, FieldType } from "@/types/merge";
import { MergeUtils } from "@/utils/mergeUtils";
import { AIMergeService } from "@/services/aiMergeService";
import { fetchFreshEntityData, supportsEntityFetching } from "@/services/entityFetcher";
import { supabase } from "@/integrations/supabase/client";

interface Document {
    id: string;
    name: string;
    category: string;
    exclude_from_ai: boolean;
    created_at: string;
    storage_id: string;
    comments?: string;
    entityId?: string; // Track which entity this document belongs to
}

interface MergeHandlerResult {
    redirectPath?: string;
}

interface DuplicateManagerProps<T> {
    open: boolean;
    onClose: () => void;
    selectedItems: T[]; // Fresh data from database - should come from parent component's state
    onMerge: (merged: T, sources: T[], selectedDocuments: Document[]) => void | MergeHandlerResult | Promise<void | MergeHandlerResult>;
    entityLabel?: string;
    mergeConfig?: MergeFormConfig<T>;
    fields?: string[]; // Optional: explicit list of fields to show (legacy support)
    isLoadingFreshData?: boolean; // Loading state for fresh data fetching
}

// Helper: get all unique fields except id/system fields
function getMergeFields<T extends object>(items: T[], mergeConfig?: MergeFormConfig<T>, overrideFields?: string[]): FieldConfig[] {
    if (mergeConfig?.fields) {
        return mergeConfig.fields.filter(field =>
            !mergeConfig.excludeFields?.includes(field.name)
        );
    }

    if (overrideFields && overrideFields.length) {
        return overrideFields.map(fieldName => ({
            name: fieldName,
            type: items.length > 0 ? MergeUtils.detectFieldType(items[0][fieldName]) : 'text' as FieldType,
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, ' '),
            mergeBehavior: MergeUtils.getSuggestedMergeBehavior(fieldName, MergeUtils.detectFieldType(items[0][fieldName])),
            aiMergeEnabled: MergeUtils.shouldEnableAIMerge(fieldName, MergeUtils.detectFieldType(items[0][fieldName]))
        }));
    }

    if (!items.length) return [];

    const systemFields = ["id", "createdAt", "created_at", "updatedAt", "updated_at", "organization_uuid"];
    const allFields = Array.from(
        new Set(
            items.flatMap(item => Object.keys(item))
        )
    );

    return allFields
        .filter(f => !systemFields.includes(f))
        .map(fieldName => ({
            name: fieldName,
            type: MergeUtils.detectFieldType(items[0][fieldName]),
            label: fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/_/g, ' '),
            mergeBehavior: MergeUtils.getSuggestedMergeBehavior(fieldName, MergeUtils.detectFieldType(items[0][fieldName])),
            aiMergeEnabled: MergeUtils.shouldEnableAIMerge(fieldName, MergeUtils.detectFieldType(items[0][fieldName]))
        }));
}

// Helper: find most recent record index
function getMostRecentIndex<T extends object>(items: T[]): number {
    const dateFields = ["updatedAt", "updated_at", "createdAt", "created_at"];
    let bestIdx = items.length - 1;
    let bestDate = null;
    for (let i = 0; i < items.length; ++i) {
        for (const field of dateFields) {
            if (items[i][field]) {
                const d = new Date(items[i][field]);
                if (!bestDate || d > bestDate) {
                    bestDate = d;
                    bestIdx = i;
                }
                break;
            }
        }
    }
    return bestIdx;
}

function DuplicateManager<T extends object>({
    open,
    onClose,
    selectedItems,
    onMerge,
    entityLabel = "items",
    mergeConfig,
    fields: overrideFields,
    isLoadingFreshData = false,
}: DuplicateManagerProps<T>) {
    const navigate = useNavigate();
    
    const fieldConfigs = useMemo(() => {
        const configs = getMergeFields(selectedItems, mergeConfig, overrideFields);
        ////console.log(`🔍 Field configs generated:', configs);
        ////console.log(`🔍 AI Analysis field config:', configs.find(f => f.name === 'ai_analysis'));
        return configs;
    }, [selectedItems, mergeConfig, overrideFields]);

    // State for excluded entities (track by index in original selectedItems array)
    const [excludedEntityIndices, setExcludedEntityIndices] = useState<Set<number>>(new Set());

    // Get filtered items (non-excluded entities only)
    const activeItems = useMemo(() => {
        return selectedItems.filter((_, index) => !excludedEntityIndices.has(index));
    }, [selectedItems, excludedEntityIndices]);

    // Create mapping from active item index to original item index
    const activeToOriginalIndex = useMemo(() => {
        const mapping: number[] = [];
        selectedItems.forEach((_, originalIndex) => {
            if (!excludedEntityIndices.has(originalIndex)) {
                mapping.push(originalIndex);
            }
        });
        return mapping;
    }, [selectedItems, excludedEntityIndices]);

    const mostRecentIdx = useMemo(() => getMostRecentIndex(activeItems), [activeItems]);// State: for each field, which record index is selected or if it's custom/AI merged
    const [selectedByField, setSelectedByField] = useState<Record<string, number>>(() => {
        const initial: Record<string, number> = {};
        fieldConfigs.forEach(field => {
            initial[field.name] = mostRecentIdx;
        });
        return initial;
    });    // Custom values (user overrides or AI merged values)
    const [customValues, setCustomValues] = useState<Record<string, any>>({});

    // Multi-selection state for combine behavior
    const [multiSelectedByField, setMultiSelectedByField] = useState<Record<string, Set<number>>>({});

    // Document names cache for storage IDs
    const [documentNames, setDocumentNames] = useState<Record<string, string>>({});

    // Document selection state
    const [allDocuments, setAllDocuments] = useState<Document[]>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
    // AI merge states
    const [aiMerging, setAiMerging] = useState<Record<string, boolean>>({});
    // Preview mode
    const [showPreview, setShowPreview] = useState(false);

    // Handle entity exclusion/restoration
    const handleEntityExclude = useCallback((originalIndex: number) => {
        setExcludedEntityIndices(prev => {
            const newSet = new Set(prev);
            // Don't allow excluding if it would leave less than 2 active entities
            if (!newSet.has(originalIndex) && selectedItems.length - newSet.size <= 2) {
                return prev; // Cannot exclude - would leave insufficient entities
            }
            newSet.add(originalIndex);
            return newSet;
        });
    }, [selectedItems.length]);

    const handleEntityRestore = useCallback((originalIndex: number) => {
        setExcludedEntityIndices(prev => {
            const newSet = new Set(prev);
            newSet.delete(originalIndex);
            return newSet;
        });
    }, []);

    // Reset exclusions when selectedItems change
    useEffect(() => {
        setExcludedEntityIndices(new Set());
    }, [selectedItems]);

    // Merge operation state
    const [isMerging, setIsMerging] = useState(false);
    const [mergeError, setMergeError] = useState<string | null>(null);// Reset selection if items or fields change and fetch documents
    useEffect(() => {
        setSelectedByField(() => {
            const initial: Record<string, number> = {};
            fieldConfigs.forEach(field => {
                initial[field.name] = mostRecentIdx;
            });
            return initial;
        }); setCustomValues({});
        setAiMerging({});
        setMultiSelectedByField({});
        setMergeError(null);

        // Fetch documents for all active entities only
        if (activeItems.length > 0) {
            fetchAllDocuments();
        }
    }, [fieldConfigs, mostRecentIdx, activeItems]);    // Fetch documents for all entities
    const fetchAllDocuments = useCallback(async () => {
        setIsLoadingDocuments(true);
        try {
            const allDocs: Document[] = [];

            for (const item of activeItems) {
                const entityId = (item as any).id;
                if (!entityId) continue;

                try {
                    const { data, error } = await supabase.functions.invoke(`document-manager?command=get-documents-for-entity&entityId=${entityId}`, {
                        method: 'GET',
                    });

                    if (error) {
                        console.error(`Error fetching documents for entity ${entityId}:`, error);
                        continue;
                    }

                    const mappedDocuments: Document[] = data.map((doc: any) => ({
                        id: doc.id,
                        name: doc.display_name || doc.name,
                        category: doc.category,
                        exclude_from_ai: doc.exclude_from_ai,
                        created_at: doc.created_at,
                        storage_id: doc.storage_id,
                        comments: doc.comments,
                        entityId: entityId,
                    }));

                    allDocs.push(...mappedDocuments);
                } catch (error) {
                    console.error(`Error fetching documents for entity ${entityId}:`, error);
                }
            }

            setAllDocuments(allDocs);
            // By default, select all documents
            setSelectedDocuments(new Set(allDocs.map(doc => doc.id)));
        } catch (error) {
            console.error('Error fetching documents:', error);        } finally {
            setIsLoadingDocuments(false);
        }
    }, [activeItems]);// Handle AI merge for a specific field


    const handleAIMerge = useCallback(async (fieldName: string) => {
        const fieldConfig = fieldConfigs.find(f => f.name === fieldName);
        if (!fieldConfig) return;

        setAiMerging(prev => ({ ...prev, [fieldName]: true }));

        try {
            // Get values based on current selection logic (same as merged object calculation)
            let valuesToMerge: any[] = [];

            const isCombineMode = fieldConfig.mergeBehavior === 'combine' || fieldConfig.mergeBehavior === 'ai';
            const multiSelected = multiSelectedByField[fieldName];
            const hasCustomValue = customValues[fieldName] !== undefined;            if (isCombineMode && multiSelected?.size > 0) {
                // Use values from multi-selected records
                const selectedIndices = Array.from(multiSelected);
                valuesToMerge = selectedIndices
                    .map(idx => activeItems[idx]?.[fieldName])
                    .filter(v => v !== null && v !== undefined);
            } else if (selectedByField[fieldName] !== undefined) {
                // Use value from single selected record
                const selectedValue = activeItems[selectedByField[fieldName]]?.[fieldName];
                if (selectedValue !== null && selectedValue !== undefined) {
                    valuesToMerge = [selectedValue];
                }
            }

            // If there's already a custom value, include it for incremental merging
            if (hasCustomValue && customValues[fieldName]) {
                valuesToMerge.unshift(customValues[fieldName]); // Add current custom value at beginning
            }

            if (valuesToMerge.length === 0) {
                console.warn(`No values selected for AI merge of field: ${fieldName}`);
                return;
            }

            const mergedValue = await AIMergeService.mergeValues(
                valuesToMerge,
                mergeConfig?.entityName || 'unknown'
            );

            setCustomValues(prev => ({
                ...prev,
                [fieldName]: mergedValue
            }));
        } catch (error) {
            console.error(`AI merge failed for ${fieldName}:`, error);        } finally {
            setAiMerging(prev => ({ ...prev, [fieldName]: false }));
        }
    }, [fieldConfigs, activeItems, selectedByField, multiSelectedByField, customValues, mergeConfig]);// Build merged object

    const merged: Partial<T> = useMemo(() => {
        const result: Partial<T> = {};


        fieldConfigs.forEach(field => {
            ////console.log(`🔍 Processing field ${field.name}: mergeBehavior=${field.mergeBehavior}, type=${field.type}`);

            if (customValues[field.name] !== undefined) {
                ////console.log(`🔍 ${field.name} using custom value:`, customValues[field.name]);
                result[field.name] = customValues[field.name];
            } else if ((field.mergeBehavior === 'combine' || field.mergeBehavior === 'ai') && multiSelectedByField[field.name]?.size > 0) {
                // Combine values from multiple selected sources - using fresh data from activeItems
                const selectedIndices = Array.from(multiSelectedByField[field.name]);
                ////console.log(`🔍 ${field.name} combine mode - selected indices:`, selectedIndices);
                const values = selectedIndices.map(idx => activeItems[idx]?.[field.name]).filter(v => v !== null && v !== undefined);
                ////console.log(`🔍 ${field.name} combine mode - raw values:`, values);
                ////console.log(`🔍 ${field.name} combine mode - filtered values:`, values);
                if (field.type === 'array') {
                    // Flatten and deduplicate arrays
                    const flattenedValues = Array.from(new Set(values.flat()));
                    ////console.log(`🔍 ${field.name} array type - flattened values:`, flattenedValues);
                    result[field.name] = flattenedValues;
                } else if (field.type === 'presence') {
                    // For presence fields with combine behavior, return array of selected values
                    ////console.log(`🔍 ${field.name} presence type with combine - setting as array:`, values);
                    result[field.name] = values;
                } else {
                    // For combine behavior on regular fields, return array of selected values
                    ////console.log(`🔍 ${field.name} regular combine - setting as array:`, values);
                    result[field.name] = values;
                }            } else {
                // Use value from selected record - fresh data from activeItems
                const selectedIndex = selectedByField[field.name];
                const selectedValue = activeItems[selectedIndex]?.[field.name];
                ////console.log(`🔍 ${field.name} single selection mode - using selectedByField[${field.name}] = ${selectedIndex}, value:`, selectedValue);
                result[field.name] = selectedValue;
            }
        });

        // Add selected documents to the merge object
        const selectedDocs = allDocuments.filter(doc => selectedDocuments.has(doc.id));
        (result as any).selectedDocuments = selectedDocs;

        ////console.log(`🔍 Final merged object:', result);
        return result;
    }, [fieldConfigs, activeItems, selectedByField, customValues, multiSelectedByField, allDocuments, selectedDocuments]);// Get display value for a field
    const getDisplayValue = useCallback((item: T, fieldName: string, fieldType: FieldType) => {
			return MergeUtils.getDisplayValue(item[fieldName], fieldType, fieldName);
		}, []);

	// Fetch document name by storage ID
	const fetchDocumentName = useCallback(async (storageId: string): Promise<string> => {
		if (documentNames[storageId])
			return documentNames[storageId];

		try {
			const { data, error } = await supabase
				.from('documents')
				.select('display_name, name')
				.eq('storage_id', storageId)
				.single();

			if (error) {
				console.error('Error fetching document name:', error);
				return storageId; // Fallback to storage ID
			}

			const docName = data?.display_name || data?.name || storageId;
			setDocumentNames(prev => ({ ...prev, [storageId]: docName }));
			return docName;
		} catch (error) {
			console.error('Error fetching document name:', error);
			return storageId; // Fallback to storage ID
		}
	}, [documentNames]);

	// Document display component
	const DocumentDisplay = useCallback(
		({ storageId, onNameFetched }: { storageId: string, onNameFetched?: (name: string) => void }) => {
			const [docName, setDocName] = useState<string>(documentNames[storageId] || 'Loading...');

			useEffect(() => {
				if (!documentNames[storageId]) {
					fetchDocumentName(storageId).then(name => {
						setDocName(name);
						onNameFetched?.(name);
					});
				}
			}, [storageId, onNameFetched]);
			return (
				<div className="flex items-center gap-2 text-blue-500">
					<FileText className="h-4 w-4" />
					<span className="text-sm">{docName}</span>
				</div>
			);
		},
		[documentNames, fetchDocumentName]
	);

	// JSON display component with expandable content
	const JsonDisplay = useCallback(({ jsonValue }: { jsonValue: string }) => {
		const [isExpanded, setIsExpanded] = useState(false);

		return (
			<div className="flex flex-col gap-1">
				<div
					className="flex items-center gap-2 text-blue-500 cursor-pointer hover:text-blue-500"
					onClick={() => setIsExpanded(!isExpanded)}
				>
					<FileText className="h-4 w-4" />
					<span className="text-sm">JSON Data</span>
					<span className="text-xs">({isExpanded ? 'Hide' : 'Show'})</span>
				</div>
				{isExpanded && (
					<div className="font-mono text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-32 overflow-y-auto border">
						{jsonValue}
					</div>
				)}            </div>
		);
	}, []);

	// Document selection handlers
	const handleDocumentToggle = useCallback((documentId: string) => {
		setSelectedDocuments(prev => {
			const newSet = new Set(prev);
			if (newSet.has(documentId)) {
				newSet.delete(documentId);
			} else {
				newSet.add(documentId);
			}
			return newSet;
		});
	}, []);

	const handleSelectAllDocuments = useCallback(() => {
		setSelectedDocuments(new Set(allDocuments.map(doc => doc.id)));
	}, [allDocuments]);

	const handleDeselectAllDocuments = useCallback(() => {
		setSelectedDocuments(new Set());
	}, []);	// Get documents grouped by entity
	const documentsByEntity = useMemo(() => {
		const grouped: Record<string, Document[]> = {};
		allDocuments.forEach(doc => {
			if (doc.entityId) {
				if (!grouped[doc.entityId]) {
					grouped[doc.entityId] = [];
				}
				grouped[doc.entityId].push(doc);
			}
		});
		return grouped;
	}, [allDocuments]);
	// Handle merge operation with proper error handling
	const handleMerge = useCallback(async () => {
		if (isMerging) return; // Prevent multiple simultaneous merges

		setIsMerging(true);
		setMergeError(null);

		try {
			const selectedDocs = allDocuments.filter(doc => selectedDocuments.has(doc.id));

			// Call onMerge with activeItems instead of all selectedItems
            const result = await Promise.resolve(onMerge(merged as T, activeItems, selectedDocs));

			// Only close modal and navigate if merge was successful (no error thrown)
			onClose();

            const redirectPath = (result as MergeHandlerResult | undefined)?.redirectPath;
            if (redirectPath)
                navigate(redirectPath);
		} catch (error) {
			// Keep modal open on error and show error message
			const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred during merge';
			setMergeError(errorMessage);
			console.error('Merge failed:', error);
		} finally {
			setIsMerging(false);
		}
	}, [isMerging, allDocuments, selectedDocuments, onMerge, merged, activeItems, onClose, navigate, entityLabel]);// Handle multi-selection toggle for combine behavior
    const handleMultiSelectionToggle = useCallback((fieldName: string, index: number) => {
        ////console.log(`🔍 handleMultiSelectionToggle called for ${fieldName}, index ${index}`);
        setMultiSelectedByField(prev => {
            const current = prev[fieldName] || new Set();
            const updated = new Set(current);

            if (updated.has(index)) {
                updated.delete(index);
                ////console.log(`🔍 Removed index ${index} from ${fieldName}, new set:`, Array.from(updated));
            } else {
                updated.add(index);
                ////console.log(`🔍 Added index ${index} to ${fieldName}, new set:`, Array.from(updated));
            }

            const newState = { ...prev, [fieldName]: updated };
            ////console.log(`🔍 New multiSelectedByField state:`, newState);
            return newState;
        });
    }, []);    // Check if field uses combine behavior
    const isFieldCombineMode = useCallback((fieldName: string) => {
        const field = fieldConfigs.find(f => f.name === fieldName);
        const isCombine = field?.mergeBehavior === 'combine' || field?.mergeBehavior === 'ai';
        ////console.log(`🔍 isFieldCombineMode(${fieldName}): field found=${!!field}, mergeBehavior=${field?.mergeBehavior}, isCombine=${isCombine}`);
        if (fieldName === 'ai_analysis') {
            ////console.log(`🔍 AI Analysis field details:`, field);
        }
        return isCombine;
    }, [fieldConfigs]);// Handle custom value change
    const handleCustomValueChange = useCallback((fieldName: string, value: any) => {
        setCustomValues(prev => ({ ...prev, [fieldName]: value }));
    }, []);    // Render field input based on type
    const renderFieldInput = useCallback((field: FieldConfig) => {
        ////console.log(`🔍 renderFieldInput called for ${field.name}`);
        const hasCustomValue = customValues[field.name] !== undefined; const isCombineMode = isFieldCombineMode(field.name);
        const multiSelected = multiSelectedByField[field.name] || new Set();
        const isAiMerging = aiMerging[field.name];

        ////console.log(`🔍 ${field.name} renderFieldInput - isCombineMode=${isCombineMode}, multiSelected.size=${multiSelected.size}, hasCustomValue=${hasCustomValue}`);

        // Get current value - either custom, multi-selected combined, or single selected
        let currentValue;
        if (hasCustomValue) {
            currentValue = customValues[field.name];        } else if (isCombineMode && multiSelected.size > 0) {
            // Show combined values from multi-selection - get fresh data from activeItems
            const selectedIndices = Array.from(multiSelected);
            const values = selectedIndices.map(idx => activeItems[idx]?.[field.name]).filter(v => v !== null && v !== undefined && v !== '');

            if (field.type === 'array') {
                currentValue = Array.from(new Set(values.flat()));
            } else {
                // For combine mode, show a preview of combined values or indicate how many are selected
                if (values.length === 0) {
                    currentValue = `${multiSelected.size} record(s) selected (all empty)`;
                } else if (values.length !== multiSelected.size) {
                    currentValue = `${values.length}/${multiSelected.size} records with values: ${values.join(', ')}`;
                } else {
                    currentValue = values.join(', ');
                }
            }
        } else {
            currentValue = activeItems[selectedByField[field.name]]?.[field.name] ?? '';
        }

        const renderInput = () => {
            switch (field.type) {

                case 'select':
                    return (
                        <Select value={String(currentValue)} onValueChange={(value) => handleCustomValueChange(field.name, value)}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select value..." />
                            </SelectTrigger>
                            <SelectContent>
                                {field.possibleValues?.map(option => (
                                    <SelectItem key={option} value={option}>{option}</SelectItem>
                                ))}
                            </SelectContent>                        </Select>
                    );

                case 'presence':
                    // For presence fields, show which record(s) are selected
                    if (isCombineMode) {
                        // Multi-selection mode for combine behavior
                        if (multiSelected.size > 0) {
                            const selectedIndices = Array.from(multiSelected).sort((a, b) => a - b);
                            return (
                                <div className="p-2 border rounded bg-gray-50">
                                    <div className="flex flex-wrap gap-1">
                                        {selectedIndices.map(idx => (
                                            <Badge key={idx} variant="outline" className="text-xs">
                                                Record {idx + 1}
                                            </Badge>
                                        ))}
                                    </div>
                                    <div className="text-xs text-gray-600 mt-1">
                                        {selectedIndices.length} record(s) selected for combine
                                    </div>
                                </div>
                            );
                        } else {
                            return (
                                <div className="p-2 border rounded bg-gray-50">
                                    <Badge variant="outline" className="text-xs text-gray-400">
                                        No Records Selected
                                    </Badge>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Click on records above to select for combine
                                    </div>
                                </div>
                            );
                        }
                    } else {
                        // Single selection mode
                        const selectedIndex = selectedByField[field.name];
                        if (selectedIndex !== undefined && selectedIndex >= 0 && selectedIndex < selectedItems.length) {
                            return (
                                <div className="p-2 border rounded bg-gray-50">
                                    <Badge variant="outline" className="text-xs">
                                        Record {selectedIndex + 1} Selected
                                    </Badge>
                                </div>
                            );
                        } else {
                            return (
                                <div className="p-2 border rounded bg-gray-50">
                                    <Badge variant="outline" className="text-xs text-gray-400">
                                        No Record Selected
                                    </Badge>
                                </div>
                            );
                        }
                    }

                case 'array':
                    return (
                        <Textarea
                            value={Array.isArray(currentValue) ? currentValue.join('\n') : String(currentValue)}
                            onChange={(e) => handleCustomValueChange(field.name, e.target.value.split('\n').map(s => s.trim()).filter(s => s))}
                            placeholder="One value per line..."
                            className="w-full font-mono text-sm"
                            rows={3}
                        />
                    );

                case 'boolean':
                    return (
                        <Select value={String(currentValue)} onValueChange={(value) => handleCustomValueChange(field.name, value === 'true')}>
                            <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="true">Yes</SelectItem>
                                <SelectItem value="false">No</SelectItem>
                            </SelectContent>
                        </Select>
                    );

                case 'json':
                    return (
                        <Textarea
                            value={typeof currentValue === 'object' ? JSON.stringify(currentValue, null, 2) : String(currentValue)}
                            onChange={(e) => {
                                try {
                                    const parsed = JSON.parse(e.target.value);
                                    handleCustomValueChange(field.name, parsed);
                                } catch {
                                    handleCustomValueChange(field.name, e.target.value);
                                }
                            }}
                            placeholder="JSON data..."
                            className="w-full font-mono text-sm"
                            rows={3}
                        />
                    );
                default:
                    return (
                        <Textarea
                            value={String(currentValue)}
                            onChange={(e) => handleCustomValueChange(field.name, e.target.value)}
                            placeholder={`Enter ${field.label.toLowerCase()}...`}
                            className="w-full"
                            rows={2}
                        />
                    );
            }
        }; return (

            <div className="space-y-2">
                {renderInput()}                
                {/* AI merge button */}
                {field.aiMergeEnabled && (
                    <div className="space-y-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAIMerge(field.name)}
                            disabled={isAiMerging}
                            className="w-full"
                        >
                            {isAiMerging ? (
                                <>
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                    AI Processing...
                                </>
                            ) : (
                                <>
                                    <Wand2 className="h-3 w-3 mr-1" />
                                    AI Merge {(() => {
                                        // Count available values for merge
                                        let valueCount = 0;
                                        const hasCustomValue = customValues[field.name] !== undefined;

                                        if (isCombineMode && multiSelected.size > 0) {
                                            valueCount = Array.from(multiSelected)
                                                .map(idx => selectedItems[idx]?.[field.name])
                                                .filter(v => v !== null && v !== undefined).length;
                                        } else if (selectedByField[field.name] !== undefined) {
                                            const selectedValue = selectedItems[selectedByField[field.name]]?.[field.name];
                                            if (selectedValue !== null && selectedValue !== undefined) {
                                                valueCount = 1;
                                            }
                                        }

                                        if (hasCustomValue) valueCount += 1; // Include existing custom value

                                        return valueCount > 0 ? `(${valueCount} values)` : '';
                                    })()}
                                </>
                            )}
                        </Button>
                        {hasCustomValue && (
                            <div className="text-xs text-blue-500 bg-blue-50 p-1 rounded text-center">
                                Will merge with existing custom value
                            </div>
                        )}
                    </div>
                )}
                {/* Required field warning */}
                {field.required && !merged[field.name] && (
                    <div className="text-xs text-red-500 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Required field
                    </div>
                )}
            </div>        );
    }, [customValues, selectedByField, activeItems, handleCustomValueChange, aiMerging, handleAIMerge, merged, getDisplayValue, multiSelectedByField, isFieldCombineMode, DocumentDisplay]);if (showPreview) {
        return (
            <Dialog open={open} onOpenChange={open => !open && onClose()}>
                <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Preview Merged {entityLabel.slice(0, -1)}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto min-h-0 p-4 bg-gray-50 rounded-sm">
                        <div className="space-y-4">
                            {fieldConfigs.map(field => {
                                const value = merged[field.name];
                                const displayValue = MergeUtils.getDisplayValue(value, field.type, field.name);

                                return (
                                    <div key={field.name} className="bg-white p-3 rounded border">
                                        <div className="flex items-center gap-2 mb-2">
                                            <label className="font-medium text-sm">{field.label}</label>
                                        </div>
                                        <div className="text-sm text-gray-700">
                                            {typeof displayValue === 'object' && displayValue?.type === 'document' ? (
                                                field.name === 'default_resume' ? (
                                                    <DocumentDisplay storageId={displayValue.value} />
                                                ) : (
                                                    <div className="flex items-center gap-2 text-blue-500">
                                                        <FileText className="h-4 w-4" />
                                                        <span className="text-sm">Document</span>
                                                    </div>
                                                )
                                            ) : (
                                                String(displayValue) || <span className="text-gray-400 italic">No value</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}                        
                            </div>
                    </div>                    
                    <DialogFooter className="flex-shrink-0">
                        <Button variant="outline" onClick={() => setShowPreview(false)}>
                            Back to Edit
                        </Button>

                        {mergeError && (
                            <div className="flex-1 px-4">
                                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                                    <strong>Merge Error:</strong> {mergeError}
                                </div>
                            </div>
                        )}

                        <Button
                            onClick={handleMerge}
                            disabled={isMerging}
                            className="bg-blue-500 hover:bg-blue-500 text-white disabled:opacity-50"
                        >
                            {isMerging ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Merging...
                                </>
                            ) : (
                                'Confirm Merge'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }
    return (
        <Dialog open={open} onOpenChange={open => !open && onClose()}>
            <DialogContent className="max-w-[95vw] w-[95vw] max-h-[95vh] h-[95vh] flex flex-col">                <DialogHeader className="flex-shrink-0">
                    <DialogTitle>Merge {activeItems.length} {entityLabel}</DialogTitle>
                    <div className="text-sm text-gray-600 space-y-1">
                        <p>Select values to merge by clicking on source records or entering custom values.</p>
                        <div className="flex gap-4 text-xs">
                            <span>✓ = Single selection</span>
                            <span>● = Multi-selection (combine mode)</span>
                        </div>
                        {excludedEntityIndices.size > 0 && (
                            <div className="text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                                <span className="font-medium">{excludedEntityIndices.size} entity(ies) excluded from merge</span>
                            </div>
                        )}
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto min-h-0">
                    {/* Loading state for fresh data */}
                    {isLoadingFreshData && (
                        <div className="flex items-center justify-center py-8 bg-blue-50 rounded-sm">
                            <div className="flex items-center gap-3 text-blue-500">
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span className="font-medium">Loading fresh {entityLabel} data from database...</span>
                            </div>
                        </div>
                    )}                    {/* Main content - only show when not loading */}
                    {!isLoadingFreshData && (
                        <div className="space-y-6">                            {/* Excluded entities section */}
                            {excludedEntityIndices.size > 0 && (
                                <div className="bg-gray-50 border rounded-sm p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="font-medium text-gray-900">Excluded Entities</h3>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="secondary">{excludedEntityIndices.size} excluded</Badge>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => setExcludedEntityIndices(new Set())}
                                                className="text-blue-500 hover:text-blue-500"
                                            >
                                                Restore All
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="grid gap-2">
                                        {Array.from(excludedEntityIndices).map(originalIndex => {
                                            const item = selectedItems[originalIndex];
                                            return (
                                                <div key={originalIndex} className="flex items-center justify-between bg-white border rounded p-2">
                                                    <span className="text-sm text-gray-600">
                                                        Record {originalIndex + 1}: {(item as any)?.name || (item as any)?.email || (item as any)?.id || 'Unknown'}
                                                    </span>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => handleEntityRestore(originalIndex)}
                                                        className="text-green-600 hover:text-green-700"
                                                    >
                                                        Restore
                                                    </Button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div className="overflow-x-auto border rounded-sm">
                                <table className="min-w-full text-sm">
                                    <thead>
                                        <tr>
                                            <th className="border px-2 py-1 text-left bg-gray-50 w-48">Field</th>
                                            {activeItems.map((item, activeIdx) => {
                                                const originalIndex = activeToOriginalIndex[activeIdx];
                                                const canExclude = activeItems.length > 2;
                                                return (
                                                    <th key={activeIdx} className="border px-2 py-1 bg-gray-50 min-w-40">
                                                        <div className="flex items-center justify-between">
                                                            <span>Record {originalIndex + 1}</span>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleEntityExclude(originalIndex)}
                                                                disabled={!canExclude}
                                                                className={`h-6 w-6 p-0 ${canExclude ? 'text-red-500 hover:text-red-700 hover:bg-red-50' : 'text-gray-300 cursor-not-allowed'}`}
                                                                title={canExclude ? 'Exclude this entity from merge' : 'Cannot exclude - at least 2 entities required'}
                                                            >
                                                                <X className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                            <th className="border px-2 py-1 bg-gray-50 min-w-60">Value</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fieldConfigs.map(field => {
                                            const hasCustomValue = customValues[field.name] !== undefined;
                                            const isCombineMode = isFieldCombineMode(field.name);
                                            const multiSelected = multiSelectedByField[field.name] || new Set();

                                            return (
                                                <tr key={field.name}>
                                                    <td className="border px-2 py-1 font-medium bg-gray-50 align-top">
                                                        <div className="space-y-1">
                                                            <div className="font-medium flex items-center gap-2">
                                                                {field.label}
                                                                {isCombineMode && (
                                                                    <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-500">
                                                                        Multi-select
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            {field.description && (
                                                                <div className="text-xs text-gray-500">{field.description}</div>
                                                            )}
                                                        </div>                                                    </td>
                                                    {activeItems.map((item, activeIdx) => {
                                                        const displayValue = getDisplayValue(item, field.name, field.type);
                                                        let isSelected = false;

                                                        if (isCombineMode) {
                                                            isSelected = multiSelected.has(activeIdx);
                                                        } else {
                                                            isSelected = selectedByField[field.name] === activeIdx && !hasCustomValue;
                                                        }

                                                        return (
                                                            <td
                                                                key={activeIdx}
                                                                className={`border px-2 py-1 cursor-pointer transition-colors align-top ${isSelected
                                                                        ? 'bg-green-100 border-green-500'
                                                                        : 'hover:bg-gray-100'
                                                                    }`} onClick={() => {
                                                                        ////console.log(`🔍 Cell clicked for ${field.name}, index ${activeIdx}, isCombineMode=${isCombineMode}`);
                                                                        if (isCombineMode) {
                                                                            ////console.log(`🔍 Calling handleMultiSelectionToggle for ${field.name}, index ${activeIdx}`);
                                                                            handleMultiSelectionToggle(field.name, activeIdx);
                                                                        } else {
                                                                            setSelectedByField(sel => ({ ...sel, [field.name]: activeIdx }));
                                                                            // Clear custom value when selecting from source
                                                                            if (hasCustomValue) {
                                                                                setCustomValues(prev => {
                                                                                    const newValues = { ...prev };
                                                                                    delete newValues[field.name];
                                                                                    return newValues;
                                                                                });
                                                                            }
                                                                        }
                                                                    }}
                                                            >
                                                                <div className="min-h-8 break-words">
                                                                    {field.type === 'array' && Array.isArray(item[field.name]) ? (
                                                                        <div className="font-mono text-xs bg-gray-50 p-2 rounded whitespace-pre-wrap max-h-20 overflow-y-auto">
                                                                            {item[field.name].join('\n')}
                                                                        </div>
                                                                    ) : (<>
                                                                        {typeof displayValue === 'object' && displayValue?.type === 'document' ? (
                                                                            field.name === 'default_resume' ? (
                                                                                <DocumentDisplay storageId={displayValue.value} />
                                                                            ) : (
                                                                                <JsonDisplay jsonValue={displayValue.value} />
                                                                            )
                                                                        ) : (
                                                                            displayValue || <span className="text-gray-400 italic">Empty</span>
                                                                        )}
                                                                    </>
                                                                    )}
                                                                    {isSelected && (
                                                                        <span className="ml-1 text-green-600 font-bold">
                                                                            {isCombineMode ? '●' : '✓'}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        );
                                                    })}
                                                    <td className="border px-2 py-1 align-top">
                                                        {renderFieldInput(field)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>						
                                    </table>
                            </div>

                            {/* Document Selection Section */}
                            {allDocuments.length > 0 && (
                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-lg font-medium">Documents to Keep</h3>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleSelectAllDocuments}
                                                disabled={selectedDocuments.size === allDocuments.length}
                                            >
                                                Select All
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleDeselectAllDocuments}
                                                disabled={selectedDocuments.size === 0}
                                            >
                                                Deselect All
                                            </Button>
                                        </div>
                                    </div>

                                    {isLoadingDocuments ? (
                                        <div className="flex items-center justify-center py-8 bg-gray-50 rounded-sm">
                                            <div className="flex items-center gap-3 text-gray-600">
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <span>Loading documents...</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="border rounded-sm max-h-60 overflow-y-auto">
                                            <table className="min-w-full text-sm">
                                                <thead>
                                                    <tr className="bg-gray-50">
                                                        <th className="border-b px-3 py-2 text-left w-12">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedDocuments.size === allDocuments.length && allDocuments.length > 0}
                                                                onChange={selectedDocuments.size === allDocuments.length ? handleDeselectAllDocuments : handleSelectAllDocuments}
                                                                className="rounded"
                                                            />
                                                        </th>
                                                        <th className="border-b px-3 py-2 text-left">Document Name</th>
                                                        <th className="border-b px-3 py-2 text-left">Category</th>
                                                        <th className="border-b px-3 py-2 text-left">Source Entity</th>
                                                        <th className="border-b px-3 py-2 text-left">Created</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {allDocuments.map((doc, idx) => {
                                                        const isSelected = selectedDocuments.has(doc.id);
                                                        const sourceEntity = selectedItems.find((item: any) => item.id === doc.entityId);

                                                        return (                                                            <tr
                                                                key={doc.id}
                                                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                                                                onClick={() => handleDocumentToggle(doc.id)}
                                                            >
                                                                <td className="border-b px-3 py-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isSelected}
                                                                        onChange={() => handleDocumentToggle(doc.id)}
                                                                        className="rounded"
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    />
                                                                </td>
                                                                <td className="border-b px-3 py-2">
                                                                    <div className="flex items-center gap-2">
                                                                        <FileText className="h-4 w-4 text-blue-500" />
                                                                        <span className="font-medium">{doc.name}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="border-b px-3 py-2">
                                                                    <Badge variant="outline" className="text-xs">
                                                                        {doc.category}
                                                                    </Badge>
                                                                </td>
                                                                <td className="border-b px-3 py-2 text-gray-600">
                                                                    {sourceEntity ? `Record ${activeItems.indexOf(sourceEntity) + 1}` : 'Unknown'}
                                                                </td>
                                                                <td className="border-b px-3 py-2 text-gray-500 text-xs">
                                                                    {new Date(doc.created_at).toLocaleDateString()}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    <div className="mt-2 text-sm text-gray-600">
                                        {selectedDocuments.size} of {allDocuments.length} documents selected
                                    </div>
                                </div>)}
                        </div>
                    )}
                </div>				<DialogFooter className="flex-shrink-0 flex justify-between">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setShowPreview(true)}
                        >
                            Preview Result
                        </Button>

                        {mergeError && (
                            <div className="flex-1 px-4">
                                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                                    <strong>Error:</strong> {mergeError}
                                </div>
                            </div>
                        )}

                        <Button
                            onClick={handleMerge}
                            disabled={isMerging}
                            className="bg-blue-500 hover:bg-blue-500 text-white disabled:opacity-50"
                        >
                            {isMerging ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Merging...
                                </>
                            ) : (
                                'Merge Now'
                            )}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DuplicateManager;

// Enhanced useDuplicateManager hook with generic fresh data fetching
// This hook now fetches fresh data from the database when the merge modal opens
// to ensure that merge operations use the most up-to-date data instead of 
// potentially stale UI state. Supports all entity types with registered fetchers.
export function useDuplicateManager<T>(allItems: T[], isSelected: (id: string) => boolean, entityLabel?: string, mergeConfig?: MergeFormConfig<T>) {
    const [showMergeModal, setShowMergeModal] = useState(false);
    const [freshSelectedItems, setFreshSelectedItems] = useState<T[]>([]);
    const [isLoadingFreshData, setIsLoadingFreshData] = useState(false);

    // Get initially selected items from UI state (for fallback)
    const selectedItems = allItems.filter((item: any) => isSelected(item.id));

    const openMergeModal = useCallback(async () => {
        setShowMergeModal(true);

        // Check if we have fresh data fetching support for this entity type
        if (entityLabel && supportsEntityFetching(entityLabel) && selectedItems.length > 0) {
            setIsLoadingFreshData(true);
            try {
                const selectedIds = selectedItems.map((item: any) => item.id);
                const freshData = await fetchFreshEntityData<T>(entityLabel, selectedIds);

                setFreshSelectedItems(freshData);
                ////console.log(`✅ Fresh ${entityLabel} data loaded for merge:`, freshData.length, "items");
            } catch (error) {
                console.error(`Failed to fetch fresh ${entityLabel} data, using UI state as fallback:`, error);
                setFreshSelectedItems(selectedItems);
            } finally {
                setIsLoadingFreshData(false);
            }
        } else {
            // For unsupported entity types or if no items selected, use UI state
            if (entityLabel && !supportsEntityFetching(entityLabel)) {
                ////console.log(`ℹ️ Entity type "${entityLabel}" does not support fresh data fetching, using UI state`);
            }
            setFreshSelectedItems(selectedItems);
        }
    }, [selectedItems, entityLabel]);

    const closeMergeModal = useCallback(() => {
        setShowMergeModal(false);
        setFreshSelectedItems([]);
        setIsLoadingFreshData(false);
    }, []);

    const getDuplicateManagerProps = (onMerge: (merged: T, sources: T[], selectedDocuments: Document[]) => void | MergeHandlerResult | Promise<void | MergeHandlerResult>, fields?: string[]) => ({
        open: showMergeModal,
        onClose: closeMergeModal,
        selectedItems: freshSelectedItems.length > 0 ? freshSelectedItems : selectedItems, // Use fresh data if available
        onMerge,
        entityLabel,
        mergeConfig,
        fields,
        isLoadingFreshData, // Pass loading state to component
    });

    return {
        showMergeModal,
        openMergeModal,
        closeMergeModal,
        selectedItems: freshSelectedItems.length > 0 ? freshSelectedItems : selectedItems,
        getDuplicateManagerProps,
        isLoadingFreshData,
    };
}